import { useEffect, useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { Route as RootRoute } from "./__root";
import { useAuth } from "../providers/auth-context";

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: "/auth/login",
  component: LoginPage,
});

const ERROR_MESSAGES: Record<string, string> = {
  no_session: "セッションが確立できませんでした。もう一度ログインしてください。",
  not_registered: "このアカウントは登録されていません。管理者から招待コードを受け取ってください。",
  account_not_linked: "アカウントが紐付いていません。管理者に連絡してください。",
  access_denied: "ログインがキャンセルされました。",
  signup_disabled: "新規登録が無効化されています。管理者から招待を受けてください。",
  please_restart_the_process: "ログインセッションが無効になりました。再度ログインしてください。",
  invalid_code:
    "LINE 認証コードの検証に失敗しました。もう一度ログインしてください。解消しない場合は管理者に連絡してください。",
  state_mismatch:
    "ログイン状態の確認に失敗しました。ブラウザの Cookie 設定を確認し、もう一度ログインしてください。",
  no_code: "LINE から認証コードを受け取れませんでした。もう一度ログインしてください。",
  invalid_callback_request:
    "LINE ログインの戻り値を処理できませんでした。もう一度ログインしてください。",
  oauth_provider_not_found: "LINE ログイン設定が見つかりません。管理者に連絡してください。",
  no_callback_url: "ログイン後の戻り先を確認できませんでした。管理者に連絡してください。",
  unable_to_get_user_info:
    "LINE アカウント情報の取得に失敗しました。もう一度ログインしてください。解消しない場合は管理者に連絡してください。",
  email_not_found:
    "LINE アカウント情報の取得に失敗しました。もう一度ログインしてください。解消しない場合は管理者に連絡してください。",
  unable_to_link_account: "LINE アカウントの紐付けに失敗しました。管理者に連絡してください。",
  account_already_linked_to_different_user:
    "この LINE アカウントは別のユーザーに紐付いています。管理者に連絡してください。",
  "email_doesn't_match": "LINE アカウントのメール情報が一致しません。管理者に連絡してください。",
  internal_server_error:
    "ログイン処理中にサーバーエラーが発生しました。時間を置いて再度ログインしてください。",
  unknown_error: "ログイン処理中にエラーが発生しました。",
};

function resolveErrorMessage(code: string | null): string | null {
  if (!code) return null;
  return ERROR_MESSAGES[code] ?? ERROR_MESSAGES.unknown_error!;
}

function LoginPage() {
  const { sessionExpired, signInWithLine } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [lineState, setLineState] = useState<"base" | "hover" | "press">("base");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setError(resolveErrorMessage(params.get("error")));
  }, []);

  const visibleError =
    error ??
    (sessionExpired ? "セッションの有効期限が切れました。LINE で再ログインしてください。" : null);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "var(--f-sans)",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 400,
          background: "var(--bg-raised)",
          border: "0.5px solid var(--rule)",
          borderRadius: "var(--radius-lg)",
          padding: "48px 40px 40px",
        }}
      >
        {/* ロゴ */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            marginBottom: 36,
          }}
        >
          <img
            src="/images/system-logo.png"
            alt="CarryPilot"
            style={{ width: 72, height: 72, display: "block" }}
          />
          <div
            style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-.02em", color: "var(--ink)" }}
          >
            Carry<span style={{ color: "var(--accent)" }}>Pilot</span>
          </div>
        </div>

        {visibleError && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: "10px 14px",
              background: "oklch(0.97 0.02 25)",
              border: "0.5px solid var(--danger)",
              borderRadius: "var(--radius)",
              fontSize: "var(--fs-sm)",
              color: "var(--danger)",
              lineHeight: 1.5,
            }}
          >
            {visibleError}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={() => void signInWithLine()}
            onMouseEnter={() => setLineState("hover")}
            onMouseLeave={() => setLineState("base")}
            onMouseDown={() => setLineState("press")}
            onMouseUp={() => setLineState("hover")}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              width: "100%",
              height: 42,
              background:
                lineState === "press" ? "#048B3C" : lineState === "hover" ? "#05B34D" : "#06C755",
              color: "#fff",
              border: 0,
              borderRadius: "var(--radius)",
              fontSize: "var(--fs-md)",
              fontWeight: 600,
              letterSpacing: ".01em",
              cursor: "pointer",
              transition: "background 0.1s",
            }}
          >
            <img
              src="/images/line/line_icon.png"
              srcSet="/images/line/line_icon@2x.png 2x"
              alt=""
              style={{ display: "block", height: 22, width: "auto" }}
            />
            LINE でログイン
          </button>
        </div>

        <div
          style={{
            marginTop: 28,
            paddingTop: 20,
            borderTop: "0.5px solid var(--rule-soft)",
            fontSize: "var(--fs-xs)",
            color: "var(--ink-4)",
            lineHeight: 1.6,
          }}
        >
          初回ログイン時は管理者から発行された招待コードが必要です。
        </div>
      </div>
    </div>
  );
}

export { resolveErrorMessage };
